/**
 * The volunteer profile (M1 track D3).
 *
 * Read by default, edited on request. The two modes share one component because
 * they show the same fields, and a separate edit page is where "the form has a
 * field the profile does not display" starts.
 *
 * The edit sends only the fields that changed. That is not an optimisation — the
 * API's `PATCH` treats an absent key as "leave it alone" and a null as "clear
 * it", so sending the whole form back would let this screen blank the twelve
 * columns it does not render (address lines, education, alternate phone) the
 * first time anyone saves.
 */

import { useState } from 'react';
// `Link` is gone: it existed only for the "← Back to volunteers" paragraph the
// breadcrumb replaced.
import { useParams } from 'react-router-dom';
import {
  AGE_GROUP_LABELS,
  AGE_GROUPS,
  ageGroupLabel,
  OCCUPATION,
  OCCUPATION_LABELS,
  OCCUPATIONS,
  VOLUNTEER_CATEGORIES,
  VOLUNTEER_CATEGORY,
  VOLUNTEER_ADMIN_STATUS_TARGETS,
  VOLUNTEER_STATUS,
  VOLUNTEER_STATUS_TRANSITIONS,
  decodeLanguageSkills,
  joinCodes,
  occupationLabel,
  splitCodes,
  trainingStatus,
} from '@parinaam-vms/shared';
import type {
  ReferenceValue,
  TrainingStatus,
  Volunteer,
  VolunteerStatus,
} from '@parinaam-vms/shared';
import {
  Button,
  Card,
  CheckboxGroup,
  DataTable,
  DetailList,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  SelectInput,
  StatusBadge,
  TextArea,
  useToast,
  TextInput,
} from '../../components/common';
import type { SelectOption } from '../../components/common';
import { HistoryPanel } from '../../components/history/HistoryPanel';
import { VolunteerHours } from '../../components/attendance/VolunteerHours';
import { OrganizationPicker } from '../../components/organizations/OrganizationPicker';
import { useAuth } from '../../hooks/useAuth';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useReferenceValuesGrouped } from '../../hooks/useReferenceValues';
import {
  useChangeVolunteerStatus,
  useUpdateVolunteer,
  useVolunteer,
  useVolunteerAssignments,
  useVolunteerAttendance,
  useVolunteerHistory,
} from '../../hooks/useVolunteers';
import type { VolunteerStatusAction } from '../../hooks/useVolunteers';
import type { VolunteerUpdateBody } from '../../services/api/volunteers';
import { contactProblems } from '../../utils/contact';
import { formatDate, formatDateTime, formatName, humaniseCode, orDash } from '../../utils/format';
// `formatDuration` is gone: M3 moved the hours block into <VolunteerHours>.
import { CRUMB } from '../../constants/breadcrumbs';
import { DuplicateWarning } from './DuplicateWarning';
import { RecordAttendancePanel } from './RecordAttendancePanel';
import { RemoveRecordPanel } from './RemoveRecordPanel';
import styles from './VolunteerProfilePage.module.css';

export function VolunteerProfilePage() {
  const { id } = useParams<{ id: string }>();
  const [editing, setEditing] = useState(false);

  const query = useVolunteer(id);
  const reference = useReferenceValuesGrouped();
  const assignments = useVolunteerAssignments(id);
  const attendance = useVolunteerAttendance(id);
  const history = useVolunteerHistory(id);
  const organizations = useOrganizations({ include_inactive: true });

  if (query.isPending) return <LoadingState label="Loading the volunteer…" />;
  if (query.isError) {
    return (
      <>
        <PageHeader title="Volunteer" breadcrumbs={[CRUMB.volunteers, { label: 'Volunteer' }]} />
        <Card>
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </Card>
      </>
    );
  }

  const volunteer = query.data as Volunteer;

  return (
    <>
      <PageHeader
        title={formatName(volunteer)}
        breadcrumbs={[CRUMB.volunteers, { label: formatName(volunteer) }]}
        description={`${volunteer.volunteer_code} · added ${formatDate(volunteer.created_at)} · ${humaniseCode(volunteer.source)}`}
        actions={
          <div className={styles.headerActions}>
            <StatusBadge status={volunteer.status} />
            {!editing && <Button onClick={() => setEditing(true)}>Edit details</Button>}
          </div>
        }
      />

      {/*
        The decision, beside the record rather than inside the edit form.

        Only the moves this volunteer can actually make are offered, from the
        shared transition map — which has no route back to `PENDING` from
        anywhere, so "un-deciding" is not an option that exists to be clicked.
      */}
      {!editing && <StatusActions volunteer={volunteer} />}

      {/*
        Setting the status outright (Parinaam, 22 Sep 2026). Admin-only, so it is
        invisible to a TEAM account — but drawn on every record, whatever its
        status, which is the difference from the panel it replaced.
      */}
      {!editing && <AdminStatusPanel volunteer={volunteer} />}

      {/*
        Plan D12. Shown above everything else, because the whole point is that
        somebody sees it before deciding — a duplicate activated by habit is the
        outcome the flag exists to prevent.
      */}
      <DuplicateWarning volunteer={volunteer} />

      {editing ? (
        <EditForm
          volunteer={volunteer}
          reference={reference.data}
          onClose={() => setEditing(false)}
        />
      ) : (
        <Card title="Details">
          <DetailList
            items={[
              { label: 'Full name', value: formatName(volunteer) },
              { label: 'Kind of volunteer', value: humaniseCode(volunteer.category) },
              {
                label: 'Organisation',
                value: organizationName(organizations.data, volunteer.organization_id),
              },
              { label: 'Preferred name', value: orDash(volunteer.preferred_name) },
              { label: 'Email', value: orDash(volunteer.email) },
              { label: 'Phone', value: orDash(volunteer.phone) },
              { label: 'Age group', value: orDash(ageGroupLabel(volunteer.age_group)) },
              { label: 'Gender', value: labelFor(reference.data, 'GENDER', volunteer.gender) },
              { label: 'City', value: orDash(volunteer.city) },
              {
                label: 'Occupation',
                // OTHER shows what they typed; every other code shows its label.
                value:
                  volunteer.occupation === OCCUPATION.OTHER
                    ? orDash(volunteer.occupation_other)
                    : orDash(occupationLabel(volunteer.occupation)),
              },
              // The free-text column, which means "where this person works" — a
              // different fact from the partner FK above, and confusing while the
              // two shared a label.
              { label: 'Workplace', value: orDash(volunteer.organization) },
              // Carried across from a student's registration. Beside Workplace
              // because they answer the same question about different people,
              // and separate from Education, which is the qualification.
              {
                label: 'Institution',
                value: labelFor(reference.data, 'INSTITUTION', volunteer.institution),
              },
              // Carried across from the registration. Blank for anyone staff
              // entered directly, who were never asked.
              {
                label: 'Heard about us via',
                value: labelFor(reference.data, 'REFERRAL_SOURCE', volunteer.referral_source),
              },
              {
                label: 'Areas of interest',
                value: labelsFor(reference.data, 'AREA_OF_INTEREST', volunteer.areas_of_interest),
              },
              {
                label: 'Skills offered',
                value: labelsFor(reference.data, 'SKILL', volunteer.skill_areas),
              },
              {
                label: 'How often',
                // OTHER shows what they typed; every other code is humanised.
                value:
                  volunteer.volunteering_frequency === 'OTHER'
                    ? orDash(volunteer.volunteering_frequency_other)
                    : volunteer.volunteering_frequency
                      ? humaniseCode(volunteer.volunteering_frequency)
                      : '—',
              },
              {
                label: 'Languages',
                value: labelsFor(reference.data, 'LANGUAGE', volunteer.languages),
              },
              {
                label: 'Languages (read / write / speak)',
                value: languageSkillsSummary(reference.data, volunteer.language_skills),
                wide: true,
              },
              {
                label: 'Mandatory training',
                value: trainingSummary(volunteer.training_completed_on),
              },
              { label: 'Notes', value: orDash(volunteer.skills), wide: true },
              {
                label: 'Last updated',
                value: `${formatDateTime(volunteer.updated_at)}${volunteer.updated_by ? ` by ${volunteer.updated_by}` : ''}`,
                wide: true,
              },
            ]}
          />
        </Card>
      )}

      <Card title="Assignments">
        {assignments.isPending ? (
          <LoadingState />
        ) : assignments.isError ? (
          <ErrorState error={assignments.error} onRetry={() => void assignments.refetch()} />
        ) : (assignments.data ?? []).length === 0 ? (
          <EmptyState
            title="No assignments yet"
            message="Assign this volunteer to an activity from the activity screen."
          />
        ) : (
          <DataTable
            caption="Assignments"
            rows={assignments.data ?? []}
            getRowKey={(row) => row.assignment_id}
            columns={[
              { key: 'activity', header: 'Activity', render: (row) => row.activity_name },
              { key: 'program', header: 'Program', render: (row) => row.program_name },
              { key: 'date', header: 'Date', render: (row) => formatDate(row.session_date) },
              {
                key: 'status',
                header: 'Status',
                render: (row) => <StatusBadge status={row.assignment_status} />,
              },
            ]}
          />
        )}
      </Card>

      {/*
        Above the attendance list, not below it: the panel is a thing to do, and
        the list is the result of having done it. Reading order follows that.
      */}
      <RecordAttendancePanel volunteer={volunteer} />

      <Card title="Attendance">
        {attendance.isPending ? (
          <LoadingState />
        ) : attendance.isError ? (
          <ErrorState error={attendance.error} onRetry={() => void attendance.refetch()} />
        ) : (attendance.data ?? []).length === 0 ? (
          <EmptyState
            title="No attendance recorded"
            message="Hours recorded against an activity will appear here."
          />
        ) : (
          <DataTable
            caption="Attendance"
            rows={attendance.data ?? []}
            getRowKey={(row) => row.attendance_id}
            columns={[
              { key: 'date', header: 'Date', render: (row) => formatDate(row.attendance_date) },
              { key: 'activity', header: 'Activity', render: (row) => row.activity_name },
              {
                key: 'duration',
                header: 'Duration',
                numeric: true,
                // Minutes are the stored contract (Plan D9); '2h 30m' is what a
                // person reads. The conversion lives in the shared package so
                // every screen agrees.
                render: (row) => <VolunteerHours record={row} />,
              },
              {
                key: 'status',
                header: 'Status',
                render: (row) => <StatusBadge status={row.status} />,
              },
            ]}
          />
        )}
      </Card>

      {/*
        The audit trail, below the record it describes and above the delete that
        would end it. §11.53: point at a volunteer and answer "who changed this,
        and why?" without opening a database client.
      */}
      <HistoryPanel
        entries={history.data}
        isPending={history.isPending}
        error={history.isError ? history.error : null}
        onRetry={() => void history.refetch()}
        reference={reference.data}
        subject="this volunteer"
      />

      {/*
        Last on the page, and shown to an ADMIN for every volunteer — deleting
        where the record qualifies, and saying which guard is in the way where
        it does not. §5.8 still protects everyone who has actually volunteered;
        what changed is that the screen now says so instead of going blank.
      */}
      <RemoveRecordPanel
        volunteer={volunteer}
        assignments={assignments.data ?? []}
        attendance={attendance.data ?? []}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Edit
// ---------------------------------------------------------------------------

/** The occupation dropdown's options, from the shared codes (same list as intake). */
const OCCUPATION_OPTIONS: SelectOption[] = OCCUPATIONS.map((code) => ({
  value: code,
  label: OCCUPATION_LABELS[code],
}));

/** The age bands (migration 037), same list the registration form offers. */
const AGE_GROUP_OPTIONS: SelectOption[] = AGE_GROUPS.map((code) => ({
  value: code,
  label: AGE_GROUP_LABELS[code],
}));

/** Only the fields this form renders. Anything absent here is never sent. */
const EDITABLE_TEXT_FIELDS = [
  'first_name',
  'last_name',
  'preferred_name',
  'email',
  'phone',
  'city',
  'occupation',
  // The specify box behind an OTHER occupation. A plain text field like the rest;
  // the select below decides when it is shown.
  'occupation_other',
  'organization',
  'training_completed_on',
  'skills',
] as const;

type EditableTextField = (typeof EDITABLE_TEXT_FIELDS)[number];

interface EditFormProps {
  volunteer: Volunteer;
  reference: Record<string, ReferenceValue[]> | undefined;
  onClose: () => void;
}

function EditForm({ volunteer, reference, onClose }: EditFormProps) {
  const mutation = useUpdateVolunteer(volunteer.volunteer_id);
  const toast = useToast();

  const [form, setForm] = useState(() => ({
    ...(Object.fromEntries(
      EDITABLE_TEXT_FIELDS.map((field) => [field, volunteer[field] ?? ''])
    ) as Record<EditableTextField, string>),
    age_group: volunteer.age_group ?? '',
    gender: volunteer.gender ?? '',
    languages: splitCodes(volunteer.languages),
    areas_of_interest: splitCodes(volunteer.areas_of_interest),
    category: volunteer.category as string,
    organization_id: volunteer.organization_id ?? '',
  }));

  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (field: string, value: string | string[]) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: '' }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const problems: Record<string, string> = {};
    if (form.first_name.trim() === '') {
      problems.first_name = 'A volunteer needs at least a first name.';
    }
    /*
      Required here as well as on the way in (11 Sep 2026). A rule that only
      guards creation guards nothing: the same member of staff can open the
      record a minute later and empty both fields, and the volunteer becomes
      uncontactable without anything having been refused.

      `updateVolunteerSchema` refuses a blank or a null for the same reason, so
      this is the message rather than the protection.
    */
    Object.assign(problems, contactProblems(form.email, form.phone));

    if (Object.keys(problems).length > 0) {
      setErrors(problems);
      return;
    }

    const changes = diff(volunteer, form);
    if (Object.keys(changes).length === 0) {
      // Closing in silence was indistinguishable from a save that worked, so
      // the one case where nothing reaches the server is the case that most
      // needs saying out loud.
      toast.info('No changes to save.');
      onClose();
      return;
    }

    // The toast belongs to `useUpdateVolunteer`, not here. Reporting it in both
    // places is what made "Details saved." appear twice: React Query runs the
    // hook's `onSuccess` and this one.
    mutation.mutate(changes, { onSuccess: onClose });
  };

  const options = (category: string): SelectOption[] =>
    (reference?.[category] ?? []).map((value) => ({ value: value.code, label: value.label }));

  return (
    <Card title="Edit details">
      <form onSubmit={handleSubmit} noValidate>
        {mutation.isError && (
          <p className={styles.error} role="alert">
            {(mutation.error as Error).message}
          </p>
        )}

        <fieldset className={styles.section} disabled={mutation.isPending}>
          <div className={styles.row}>
            <TextInput
              label="First name"
              required
              value={form.first_name}
              error={errors.first_name}
              onChange={(event) => set('first_name', event.target.value)}
            />
            <TextInput
              label="Last name"
              value={form.last_name}
              onChange={(event) => set('last_name', event.target.value)}
            />
          </div>

          <div className={styles.row}>
            <TextInput
              label="Preferred name"
              value={form.preferred_name}
              onChange={(event) => set('preferred_name', event.target.value)}
            />
            {/*
              Status is deliberately NOT a field here.

              It used to be one dropdown among thirty, which made a decision look
              like a detail — activating somebody was the same gesture as fixing a
              typo in their name, and appeared just as reversible. It is not: it
              settles their registration, sends them a welcome, and is written to
              the history as a decision somebody made.

              It now lives in the header, as named actions, next to the badge
              showing where they are. Same split attendance makes between
              correcting a figure and approving it.
            */}
          </div>

          <div className={styles.row}>
            <TextInput
              label="Email"
              type="email"
              required
              value={form.email}
              error={errors.email}
              onChange={(event) => set('email', event.target.value)}
            />
            <TextInput
              label="Phone"
              type="tel"
              required
              value={form.phone}
              error={errors.phone}
              onChange={(event) => set('phone', event.target.value)}
            />
          </div>

          <div className={styles.row}>
            <SelectInput
              label="Age group"
              placeholder="Not recorded"
              options={AGE_GROUP_OPTIONS}
              value={form.age_group}
              onChange={(event) => set('age_group', event.target.value)}
            />
            <SelectInput
              label="Gender"
              placeholder="Not recorded"
              options={options('GENDER')}
              value={form.gender}
              onChange={(event) => set('gender', event.target.value)}
            />
          </div>

          <div className={styles.row}>
            <TextInput
              label="City"
              value={form.city}
              onChange={(event) => set('city', event.target.value)}
            />
            <SelectInput
              label="Occupation"
              placeholder="Not recorded"
              options={OCCUPATION_OPTIONS}
              value={form.occupation}
              onChange={(event) => {
                const value = event.target.value;
                set('occupation', value);
                // Leaving OTHER clears the box it revealed, so a coded occupation
                // never keeps a stale "please specify" answer beneath it.
                if (value !== OCCUPATION.OTHER && form.occupation_other !== '') {
                  set('occupation_other', '');
                }
              }}
            />
          </div>

          {form.occupation === OCCUPATION.OTHER && (
            <TextInput
              label="Please specify occupation"
              value={form.occupation_other}
              onChange={(event) => set('occupation_other', event.target.value)}
            />
          )}

          <TextInput
            label="Workplace"
            value={form.organization}
            onChange={(event) => set('organization', event.target.value)}
          />

          <TextInput
            label="Mandatory training completed on"
            type="date"
            max={TODAY}
            value={form.training_completed_on}
            error={errors.training_completed_on}
            onChange={(event) => set('training_completed_on', event.target.value)}
          />

          <CheckboxGroup
            label="Areas of interest"
            options={options('AREA_OF_INTEREST')}
            value={form.areas_of_interest}
            onChange={(value) => set('areas_of_interest', value)}
          />

          <CheckboxGroup
            label="Languages"
            options={options('LANGUAGE')}
            value={form.languages}
            onChange={(value) => set('languages', value)}
          />

          {/*
            Category first: it decides whether the organization below is required,
            and what the record even is. A CORPORATE record is the company itself.
          */}
          <SelectInput
            label="Kind of volunteer"
            options={VOLUNTEER_CATEGORIES.map((value) => ({
              value,
              label: humaniseCode(value),
            }))}
            value={form.category}
            onChange={(event) => set('category', event.target.value)}
          />

          <OrganizationPicker
            value={form.organization_id}
            required={form.category !== VOLUNTEER_CATEGORY.INDIVIDUAL}
            onChange={(value) => set('organization_id', value)}
          />

          <TextArea
            label="Notes"
            rows={3}
            value={form.skills}
            onChange={(event) => set('skills', event.target.value)}
          />
        </fieldset>

        <div className={styles.actions}>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            Save changes
          </Button>
          <Button onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * The fields that actually differ, as a `PATCH` body.
 *
 * A blank input becomes `null` (clear the column), not `''` — the API would turn
 * an empty string into null anyway, but sending the intent explicitly means the
 * comparison below stays honest about what changed.
 */
function diff(volunteer: Volunteer, form: Record<string, unknown>): VolunteerUpdateBody {
  const changes: Record<string, unknown> = {};

  for (const field of EDITABLE_TEXT_FIELDS) {
    const next = String(form[field] ?? '').trim();
    const value = next === '' ? null : next;
    if (value !== (volunteer[field] ?? null)) changes[field] = value;
  }

  const ageGroup = String(form.age_group ?? '').trim() || null;
  if (ageGroup !== (volunteer.age_group ?? null)) changes.age_group = ageGroup;

  const gender = String(form.gender ?? '').trim() || null;
  if (gender !== (volunteer.gender ?? null)) changes.gender = gender;

  const languages = joinCodes(form.languages as string[]);
  if (languages !== (volunteer.languages ?? null)) changes.languages = languages;

  const areas = joinCodes(form.areas_of_interest as string[]);
  if (areas !== (volunteer.areas_of_interest ?? null)) changes.areas_of_interest = areas;

  const organization = String(form.organization_id ?? '') || null;
  if (organization !== (volunteer.organization_id ?? null)) changes.organization_id = organization;

  if (form.category !== volunteer.category) changes.category = form.category as string;

  return changes as VolunteerUpdateBody;
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

const TODAY = new Date().toISOString().slice(0, 10);

/** How each training standing reads on the profile. */
const TRAINING_LABEL: Record<TrainingStatus, string> = {
  NONE: 'Not recorded',
  CURRENT: 'in date',
  EXPIRING_SOON: 'due for renewal',
  EXPIRED: 'lapsed',
};

/** "12 Mar 2026 — in date", or "Not recorded" when there is no date. */
function trainingSummary(completedOn: string | null): string {
  const status = trainingStatus(completedOn, TODAY);
  if (status === 'NONE') return TRAINING_LABEL.NONE;
  return `${formatDate(completedOn)} — ${TRAINING_LABEL[status]}`;
}

function labelFor(
  reference: Record<string, ReferenceValue[]> | undefined,
  category: string,
  code: string | null
): string {
  if (!code) return '—';
  return reference?.[category]?.find((value) => value.code === code)?.label ?? humaniseCode(code);
}

function labelsFor(
  reference: Record<string, ReferenceValue[]> | undefined,
  category: string,
  codes: string | null
): string {
  const list = splitCodes(codes);
  if (list.length === 0) return '—';
  return list.map((code) => labelFor(reference, category, code)).join(', ');
}

/** "English (Read, Write, Speak); Hindi (Speak)" from the encoded matrix. */
function languageSkillsSummary(
  reference: Record<string, ReferenceValue[]> | undefined,
  encoded: string | null
): string {
  const map = decodeLanguageSkills(encoded);
  const entries = Object.entries(map);
  if (entries.length === 0) return '—';

  const ability: Record<string, string> = { READ: 'Read', WRITE: 'Write', SPEAK: 'Speak' };
  return entries
    .map(([code, abilities]) => {
      const language = labelFor(reference, 'LANGUAGE', code);
      return `${language} (${abilities.map((a) => ability[a] ?? a).join(', ')})`;
    })
    .join('; ');
}

/** The partner's name, for a record that points at one. */
function organizationName(
  organizations: { organization_id: string; name: string }[] | undefined,
  organizationId: string | null
): string {
  if (!organizationId) return '—';
  return organizations?.find((row) => row.organization_id === organizationId)?.name ?? '—';
}

/**
 * Activate, inactivate, reject — the decisions, as named actions.
 *
 * **Why a card and not a dropdown.** These three change what the record *is*:
 * activating settles the registration behind the person and sends them a
 * welcome; rejecting closes their submission with a reason that goes on the
 * audit row. Offered as entries in a `Status` select they looked like the
 * twelfth field of a form, and looked reversible — which for `ACTIVE → PENDING`
 * they never were.
 *
 * Which buttons appear comes from `VOLUNTEER_STATUS_TRANSITIONS`, the same map
 * the API enforces, so the screen offers exactly the moves that will be accepted
 * (§8.40) and never has to explain that something it offered was impossible.
 *
 * **Two moves out of `REJECTED` are in that map but not among these buttons.**
 * Parinaam asked for a rejection to be reversible (13 Sep 2026), and the routes
 * to `PENDING` and `INACTIVE` that answered it are admin-only. They get their
 * own panel below rather than an extra button here, because the ordinary
 * `Inactivate` button calls `/deactivate` — which is pinned to `ACTIVE` at the
 * API and would refuse a rejected volunteer regardless.
 */
function StatusActions({ volunteer }: { volunteer: Volunteer }) {
  const mutation = useChangeVolunteerStatus(volunteer.volunteer_id);
  const [confirming, setConfirming] = useState<ConfirmableAction | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>();

  const allowed = VOLUNTEER_STATUS_TRANSITIONS[volunteer.status];
  if (allowed.length === 0) return null;

  const close = () => {
    setConfirming(null);
    setReason('');
    setReasonError(undefined);
  };

  const run = (action: ConfirmableAction) => {
    // Mirrors `reasonText` in the backend validator (§8.40), so staff learn the
    // rule without waiting for a round trip.
    if (action === 'reject' && reason.trim().length < 5) {
      setReasonError('Please give a reason of at least 5 characters.');
      return;
    }

    // As with the edit form: `useChangeVolunteerStatus` announces the outcome,
    // so this only closes the panel.
    mutation.mutate({ action, reason: reason.trim() || undefined }, { onSuccess: close });
  };

  const offers = (status: string) => allowed.includes(status as Volunteer['status']);

  return (
    <Card title="Decision">
      {confirming === null ? (
        <div className={styles.statusActions}>
          <p className={styles.statusHint}>
            {volunteer.status === VOLUNTEER_STATUS.PENDING
              ? 'Nobody has decided about this volunteer yet.'
              : volunteer.status === VOLUNTEER_STATUS.ACTIVE
                ? 'Active volunteers can be assigned to activities.'
                : volunteer.status === VOLUNTEER_STATUS.REJECTED
                ? 'This person was turned down.'
                : 'The record is kept, and can return.'}
          </p>

          <div className={styles.statusButtons}>
            {offers(VOLUNTEER_STATUS.ACTIVE) && (
              <Button variant="primary" onClick={() => setConfirming('activate')}>
                Activate
              </Button>
            )}
            {/*
              `ACTIVE` only, not `offers(INACTIVE)`. The map also allows
              `REJECTED → INACTIVE` now, but that move is the admin-only reopen
              below — `/deactivate` is pinned to `ACTIVE` and would refuse it.
            */}
            {volunteer.status === VOLUNTEER_STATUS.ACTIVE && (
              <Button onClick={() => setConfirming('deactivate')}>Inactivate</Button>
            )}
            {offers(VOLUNTEER_STATUS.REJECTED) && (
              <Button variant="danger" onClick={() => setConfirming('reject')}>
                Reject
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.statusActions}>
          <p className={styles.statusHint}>{CONFIRMATIONS[confirming]}</p>

          {/* Required for a rejection, offered for the other two: "why did she
              stop volunteering" is the question this record will be asked. */}
          <TextArea
            label={confirming === 'reject' ? 'Why are they being rejected?' : 'Reason (optional)'}
            required={confirming === 'reject'}
            rows={2}
            value={reason}
            error={reasonError}
            onChange={(event) => {
              setReason(event.target.value);
              setReasonError(undefined);
            }}
          />

          <div className={styles.statusButtons}>
            <Button
              variant={confirming === 'reject' ? 'danger' : 'primary'}
              loading={mutation.isPending}
              onClick={() => run(confirming)}
            >
              {CONFIRM_LABELS[confirming]}
            </Button>
            <Button onClick={close} disabled={mutation.isPending}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * Setting the status outright — an admin's panel, on every record.
 *
 * Separate from `StatusActions` for the reason the delete panel is separate:
 * the buttons up there are everyday work for every staff member, and this is
 * not. Hiding it from a TEAM account is presentation — `POST
 * /volunteers/:id/status` refuses them outright — but the control should not be
 * sitting on the screen of somebody who cannot use it.
 *
 * **Every status is offered, from every status** (Parinaam, 22 Sep 2026). This
 * replaced a narrower panel that appeared only on a rejected record and offered
 * two destinations. The ask was plainer: a dropdown an admin can set to
 * anything, with nothing refusing the choice — so the list is
 * `VOLUNTEER_ADMIN_STATUS_TARGETS`, which is simply every status, and the
 * matching endpoint consults neither the transition map nor the training gate.
 *
 * The current status is dropped from the list rather than disabled. Selecting it
 * is a no-op the API already absorbs without writing an audit row, so offering
 * it would mean a confirmation that truthfully reports nothing happened.
 *
 * `StatusActions` above is untouched and still carries the guarded operations:
 * an admin who wants the ordinary activate — training gate, welcome email, all
 * of it — uses that button, and this panel is for when the status simply needs
 * to be something else.
 */
function AdminStatusPanel({ volunteer }: { volunteer: Volunteer }) {
  const { hasRole } = useAuth();
  const mutation = useChangeVolunteerStatus(volunteer.volunteer_id);
  const [target, setTarget] = useState<VolunteerStatus | ''>('');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>();

  if (!hasRole('ADMIN')) return null;

  const choices = VOLUNTEER_ADMIN_STATUS_TARGETS.filter((status) => status !== volunteer.status);

  const close = () => {
    setTarget('');
    setReason('');
    setReasonError(undefined);
  };

  const run = () => {
    // Mirrored from the API rather than learned from a round trip (§8.40). The
    // reason is the one thing this operation still insists on.
    if (reason.trim().length < 5) {
      setReasonError('Please give a reason of at least 5 characters.');
      return;
    }
    mutation.mutate(
      { action: 'set', status: target as VolunteerStatus, reason: reason.trim() },
      { onSuccess: close }
    );
  };

  return (
    <Card title="Change the status">
      <div className={styles.statusActions}>
        <p className={styles.statusHint}>
          Admin override: set any status directly.
        </p>

        <SelectInput
          label="Set the status to"
          placeholder="Choose a status"
          options={choices.map((status) => ({ value: status, label: humaniseCode(status) }))}
          value={target}
          onChange={(event) => setTarget(event.target.value as VolunteerStatus | '')}
        />

        {target !== '' && (
          <>
            <p className={styles.statusHint}>{ADMIN_STATUS_CONSEQUENCES[target] ?? ''}</p>

            <TextArea
              label="Why is the status being changed?"
              required
              rows={2}
              value={reason}
              error={reasonError}
              onChange={(event) => {
                setReason(event.target.value);
                setReasonError(undefined);
              }}
            />

            <div className={styles.statusButtons}>
              <Button variant="primary" loading={mutation.isPending} onClick={run}>
                {`Yes, set them to ${humaniseCode(target).toLowerCase()}`}
              </Button>
              <Button onClick={close} disabled={mutation.isPending}>
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * What actually follows from each destination, said plainly.
 *
 * The override skips the guards, so the screen is the only place left that can
 * warn anybody — in particular that `ACTIVE` here does not check the training
 * date the activate button would refuse without, and that the nightly sweep will
 * undo it if the date is genuinely missing.
 */
const ADMIN_STATUS_CONSEQUENCES: Partial<Record<VolunteerStatus, string>> = {
  PENDING:
    'They go back to the queue.',
  ACTIVE:
    "Activating does not check their training.",
  INACTIVE: 'They stay on the books but cannot be assigned to anything.',
  REJECTED:
    'They are recorded as turned away.',
};

/**
 * `Exclude<..., 'set'>` because these two maps describe the buttons in
 * `StatusActions`, and the admin override is not one of them — it has its own
 * map above, keyed by destination rather than by action, since it is the one
 * operation with more than one.
 */
type ConfirmableAction = Exclude<VolunteerStatusAction, 'set'>;

const CONFIRMATIONS: Record<ConfirmableAction, string> = {
  activate:
    'They can be assigned, and are welcomed.',
  deactivate:
    'They can no longer be assigned.',
  reject:
    'Their registration is closed with this reason.',
};

const CONFIRM_LABELS: Record<ConfirmableAction, string> = {
  activate: 'Yes, activate',
  deactivate: 'Yes, inactivate',
  reject: 'Yes, reject',
};
